import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import PCContainer from "../components/atoms/layout/pcContainer";
import LSBdDisplay02 from "../components/molecules/text/linSeedKr/Bd/lsBdDisplay02";
import LSRgH2HeadLine from "../components/molecules/text/linSeedKr/Rg/lsRgH2HeadLine";
import LSRgBody01 from "../components/molecules/text/linSeedKr/Rg/lsRgBody01";
import BannerSwiper from "../components/templates/home/bannerSwiper";

import useDisplay from "../hooks/useDisplay";
import LSRgDisplay02 from "../components/molecules/text/linSeedKr/Rg/lsRgDisplay02";
import LectureSwiper from "../components/templates/home/lectureSwiper";
import TogetherSwiper from "../components/templates/home/togetherSwiper";
import ReviewSwiper from "../components/templates/home/reviewSwiper";
import LectureItem from "../components/organisms/lecture/lectureItem";
import ReviewItem from "../components/organisms/review/reviewItem";

import { route } from "../router/route";
import OnboardingModal from "../components/templates/modals/onboardingModal";
import BannerApi from "../api/banner.api";
import LectureApi from "../api/lecture.api";
import ReviewApi from "../api/review.api";

import { errorHandler } from "../util/errorHandler";
import Empty from "../components/molecules/empty";
import ColumnApi from "../api/column.api";
import { fileUrl } from "../api/api";
import NoticeApi from "../api/notice.api";
import NoticeSwiper from "../components/templates/home/noticeSwiper";
import PopupModal from "../components/templates/modals/popupModal";
import PopupApi from "../api/popup.api";
import LSRgCaption03 from "../components/molecules/text/linSeedKr/Rg/lsRgCaption03";
import { toast } from "react-toastify";
import LSBdDisplay03 from "../components/molecules/text/linSeedKr/Bd/lsBdDisplay03";
import OnBoardingBtn from "../components/atoms/button/onBoardingBtn";
import { macOSCheck } from "../util/macOSCheck";
import Loading from "../components/templates/loading";

const Home = () => {
  const navigate = useNavigate();
  const { isMobile, isTablet, isDesktop } = useDisplay();
  const isSmall = isMobile || isTablet;

  const [bannerList, setBannerList] = useState([]);

  const [mostListenList, setMostListenList] = useState([]);
  const [mostPremiumList, setMostPremiumList] = useState([]);
  const [reviewList, setReviewList] = useState([]);
  const [togetherList, setTogetherList] = useState([]);
  const [noticeList, setNoticeList] = useState([]);

  const [isOnboardingModal, setIsOnboardingModal] = useState(false);

  const [popupList, setPopupList] = useState([]);
  const [isContentLoading, setIsContentLoading] = useState(true);
  useEffect(() => {
    const loadContent = async () => {
      try {
        await Promise.all([
          bannerListFunc(),
          lectureListFunc(),
          reviewListFunc(),
          columnListFunc(),
          noticeListFunc(),
        ]);
      } finally {
        setIsContentLoading(false);
      }
    };

    loadContent();
    getPopupList();
  }, []);

  const getPopupList = async () => {
    try {
      const popupDate = localStorage.getItem("popup_date");
      const date = toStringByFormatting(new Date());
      let hiddenPopupIds = [];

      if (popupDate !== date) {
        localStorage.removeItem("popup_list");
      } else {
        try {
          const storedPopupIds = JSON.parse(
            localStorage.getItem("popup_list") || "[]",
          );
          hiddenPopupIds = Array.isArray(storedPopupIds) ? storedPopupIds : [];
        } catch (error) {
          localStorage.removeItem("popup_list");
        }
      }

      const response = await PopupApi.getActivePopup();
      const activePopups = response.data.data.content ?? [];
      setPopupList(
        activePopups.filter(
          (popup) => !hiddenPopupIds.some((id) => Number(id) === popup.id),
        ),
      );
    } catch (error) {
      setPopupList([]);
    }
  };

  function toStringByFormatting(source, delimiter = "-") {
    const year = source.getFullYear();
    const month = leftPad(source.getMonth() + 1);
    const day = leftPad(source.getDate());

    return [year, month, day].join(delimiter);
  }

  function leftPad(value) {
    if (value >= 10) {
      return value;
    }

    return `0${value}`;
  }

  const popupCloseFunc = (id) => {
    try {
      const storedPopupIds = JSON.parse(
        localStorage.getItem("popup_list") || "[]",
      );
      const popupIds = Array.isArray(storedPopupIds) ? storedPopupIds : [];
      localStorage.setItem(
        "popup_list",
        JSON.stringify([...new Set([...popupIds, id])]),
      );
      localStorage.setItem("popup_date", toStringByFormatting(new Date()));
    } catch (error) {
      localStorage.setItem("popup_list", JSON.stringify([id]));
      localStorage.setItem("popup_date", toStringByFormatting(new Date()));
    } finally {
      setPopupList((current) => current.filter((popup) => popup.id !== id));
    }
  };

  const bannerListFunc = async () => {
    try {
      const response = (
        await BannerApi.getInfo({
          page: 1,
          size: 0,
          bannerType: "MAIN",
        })
      ).data.data.content;

      setBannerList(response ?? []);
    } catch (error) {
      errorHandler(error);
    }
  };

  const lectureListFunc = async () => {
    try {
      const mostData = {
        page: 1,
        size: 6,
        sort: "studentCount",
        orderBy: "DESC",
        activation: "Active",
        isFree: "Active",
      };
      const premiumData = {
        page: 1,
        size: 6,
        sort: "studentCount",
        orderBy: "DESC",
        activation: "Active",
        isFree: "Inactive",
        lectureCategoryType: "PAID_LECTURE",
      };
      const [mostResult, premiumResult] = await Promise.allSettled([
        LectureApi.getList(mostData),
        LectureApi.getList(premiumData),
      ]);

      if (mostResult.status === "fulfilled") {
        setMostListenList(mostResult.value.data.data.content ?? []);
      }
      if (premiumResult.status === "fulfilled") {
        setMostPremiumList(premiumResult.value.data.data.content ?? []);
      }
      const failedResult = [mostResult, premiumResult].find(
        (result) => result.status === "rejected",
      );
      if (failedResult) errorHandler(failedResult.reason);
    } catch (error) {
      errorHandler(error);
    }
  };

  const reviewListFunc = async () => {
    try {
      const data = {
        page: 1,
        size: 6,
        activation: "Active",
        mainFlag: "Active",
        lectureType: "Video",
      };
      const response = (await ReviewApi.getList(data)).data.data.content;

      setReviewList(response ?? []);
    } catch (error) {
      errorHandler(error);
    }
  };

  const columnListFunc = async () => {
    try {
      const data = {
        page: 1,
        size: 6,
        activation: "Active",
        mainFlag: "Active",
      };
      const response = (await ColumnApi.getList(data)).data.data.content;

      setTogetherList(response ?? []);
    } catch (error) {
      errorHandler(error);
    }
  };

  const noticeListFunc = async () => {
    try {
      const data = {
        page: 1,
        size: 6,
      };
      const response = (await NoticeApi.getList(data)).data.data.content;

      setNoticeList(response ?? []);
    } catch (error) {
      errorHandler(error);
    }
  };

  return (
    <>
      <BannerSwiper
        bannerList={bannerList}
        setIsOnboardingModal={() => {
          if (sessionStorage.getItem("isLogin") === "true") {
            setIsOnboardingModal(true);
            return;
          }

          toast("온보딩을 위해 로그인을 해주세요.");
          navigate(`${route.login}`);
        }}
      />

      <HomeSection $isSmall={isSmall}>
        <PCContainer
          style={{
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            padding: isSmall ? "0px 20px" : "0px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              marginTop: isMobile ? 256 : 194,
              marginBottom: isMobile ? 40 : 100,
            }}
          >
            <LSRgDisplay02
              style={{
                fontSize: isMobile ? 16 : 24,
                lineHeight: isMobile ? "22.4px" : "33.6px",
                color: "#101113",
                marginBottom: isMobile ? 16 : 30,
              }}
            >
              👀 가장 많이 듣고 있어요!
            </LSRgDisplay02>
            {isContentLoading ? (
              <Loading />
            ) : mostListenList.length > 0 ? (
              isSmall ? (
                <OverflowXHidden>
                  {mostListenList.map((item) => {
                    return <LectureItem data={item} key={item.id} />;
                  })}
                </OverflowXHidden>
              ) : (
                <LectureSwiper dataList={mostListenList} />
              )
            ) : (
              <Empty title={"등록된 강의가 없습니다."} />
            )}
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
            }}
          >
            <LSRgDisplay02
              style={{
                fontSize: isMobile ? 16 : 24,
                lineHeight: isMobile ? "22.4px" : "33.6px",
                color: "#101113",
                marginBottom: isMobile ? 16 : 30,
              }}
            >
              ✨ 가장 인기 있는 프리미엄 강의는
            </LSRgDisplay02>
            {isContentLoading ? (
              <Loading />
            ) : mostPremiumList.length > 0 ? (
              isSmall ? (
                <OverflowXHidden>
                  {mostPremiumList.map((item) => {
                    return <LectureItem data={item} key={item.id} />;
                  })}
                </OverflowXHidden>
              ) : (
                <LectureSwiper dataList={mostPremiumList} />
              )
            ) : (
              <Empty title={"등록된 강의가 없습니다."} />
            )}
          </div>

          <div
            style={{
              width: "100%",
              height: 331,

              borderRadius: 30,
              marginBottom: isSmall ? 80 : 120,
              marginTop: isSmall ? 80 : 142,
              backgroundImage: isMobile
                ? `url(/assets/images/home_onboarding_m.png)`
                : `url(/assets/images/home_onboarding.png)`,
              backgroundPosition: "center",
              backgroundSize: "cover, cover",
              backgroundRepeat: "no-repeat",
              padding: isSmall ? "40px 26px" : "74px 106px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <LSBdDisplay03
              style={{
                color: "#fff",
                marginBottom: 10,
                fontSize: isSmall ? 20 : 32,
                lineHeight: isSmall ? "28px" : "44.8px",
              }}
            >
              요즘 고민되는 주제가 있나요?
            </LSBdDisplay03>
            <LSRgH2HeadLine
              style={{
                color: "#fff",
                marginBottom: isSmall ? 20 : 32,
                fontSize: isSmall ? 14 : 18,
                lineHeight: isSmall ? "19.6px" : "25.2px",
              }}
            >
              11가지 금융 고민 온보딩을 통해
              <br />
              나에게 가장 적합한 맞춤 강의를 추천받아 보세요.
            </LSRgH2HeadLine>

            <OnBoardingBtn
              onClick={() => {
                if (sessionStorage.getItem("isLogin") === "true") {
                  setIsOnboardingModal(true);
                  return;
                }

                toast("온보딩을 위해 로그인을 해주세요.");
                navigate(`${route.login}`);
              }}
              style={{
                height: isSmall ? 36 : 45,
                width: isSmall ? 117 : 148,
              }}
            >
              <LSRgH2HeadLine
                style={{
                  color: "#101113",
                  fontSize: isSmall ? 13 : 18,
                  lineHeight: isSmall ? "15.6px" : "25.2px",
                }}
              >
                온보딩 바로가기
              </LSRgH2HeadLine>
            </OnBoardingBtn>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
            }}
          >
            <LSRgDisplay02
              style={{
                fontSize: isMobile ? 16 : 24,
                lineHeight: isMobile ? "22.4px" : "33.6px",
                color: "#101113",
                marginBottom: isMobile ? 16 : 30,
              }}
            >
              ✏️ 수강생들이 직접 작성한 후기에요
            </LSRgDisplay02>
            {isContentLoading ? (
              <Loading />
            ) : reviewList.length > 0 ? (
              isSmall ? (
                <OverflowXHidden>
                  {reviewList.map((item) => {
                    return <ReviewItem data={item} key={item.id} />;
                  })}
                </OverflowXHidden>
              ) : (
                <ReviewSwiper dataList={reviewList} />
              )
            ) : (
              <Empty title={"등록된 후기가 없습니다."} />
            )}
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              marginTop: isMobile ? 89 : 131,
            }}
          >
            <LSRgDisplay02
              style={{
                fontSize: isMobile ? 16 : 24,
                lineHeight: isMobile ? "22.4px" : "33.6px",
                color: "#101113",
                marginBottom: isMobile ? 16 : 30,
              }}
            >
              📚 일상을 변화시킬 이야기를 함께해요
            </LSRgDisplay02>
            {isContentLoading ? (
              <Loading />
            ) : togetherList.length > 0 ? (
              isSmall ? (
                togetherList.map((item) => {
                  return (
                    <div key={item.id}>
                      <SlideContainer
                        onClick={() => {
                          navigate(`${route.column_detail}/${item.id}`);
                        }}
                        style={{
                          width: "100%",
                          height: isTablet ? "200px" : "130px",
                          cursor: "pointer",
                          marginBottom: 16,
                          background: item.thumbnailServerFileNameMain
                            ? `linear-gradient(#00000080, #00000080), url(${fileUrl}/${item.thumbnailServerFileNameMain})`
                            : "#00000080",
                          backgroundPosition: "center",
                          backgroundSize: "cover, cover",
                          backgroundRepeat: "no-repeat",
                          borderRadius: 15,

                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          padding: "0px 20px",
                        }}
                      >
                        <LSRgCaption03
                          style={{
                            marginBottom: 4,
                            color: "#fff",
                          }}
                        >
                          {item.columnCategory?.title ?? "금융 칼럼"}
                        </LSRgCaption03>
                        <ColumnTitleText
                          style={{
                            color: "#fff",
                          }}
                        >
                          {item.title}
                        </ColumnTitleText>
                      </SlideContainer>
                    </div>
                  );
                })
              ) : (
                <TogetherSwiper dataList={togetherList} />
              )
            ) : (
              <Empty title={"등록된 칼럼이 없습니다."} />
            )}
          </div>
        </PCContainer>
      </HomeSection>

      <div
        style={{
          width: "100%",
          height: isMobile ? 44 : 84,
          background: "#000043",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: isSmall ? "0px 20px" : 0,
        }}
      >
        <PCContainer
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <LSBdDisplay02
              style={{
                color: "#fff",

                fontSize: isMobile ? 14 : 24,
                lineHeight: isMobile ? "19.6px" : "33.6px",
              }}
            >
              공지사항
            </LSBdDisplay02>

            <NoticeSwiper dataList={noticeList} />
          </div>
          {isSmall ? null : (
            <div
              onClick={() => {
                navigate(route.customer_service_notice);
              }}
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                background: "#fff",
                borderRadius: 8,
                minWidth: 65,
                height: 36,
                cursor: "pointer",
              }}
            >
              <LSRgBody01
                style={{
                  color: "#A4A8B1",
                  marginTop: macOSCheck() ? 0 : 2,
                }}
              >
                더보기
              </LSRgBody01>
            </div>
          )}
        </PCContainer>
      </div>

      {isOnboardingModal ? (
        <OnboardingModal setVisiable={setIsOnboardingModal} isScroll={false} />
      ) : null}

      {popupList.length > 0 ? (
        <PopupModal
          isScroll={false}
          key={popupList[0].id}
          data={popupList[0]}
          btnOnPress={() => popupCloseFunc(popupList[0].id)}
          btnConfirmPress={() => setPopupList((current) => current.slice(1))}
        />
      ) : null}
    </>
  );
};

export default Home;

const HomeSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: ${(props) =>
    props.$isSmall ? "0px 0px 80px 0px" : "0px 0px 131px 0px"};
`;

const OverflowXHidden = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  overflow-x: auto;
  width: 100%;
  justify-content: flex-start;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none; /* 인터넷 익스플로러 */
  scrollbar-width: none; /* 파이어폭스 */
`;

const ColumnTitleText = styled(LSRgH2HeadLine)`
  text-overflow: ellipsis;
  overflow: hidden;
  word-break: break-word;

  display: -webkit-box;
  -webkit-line-clamp: 2; // 원하는 라인수
  -webkit-box-orient: vertical;
`;

const SlideContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0px 80px;
  flex: 1;
  width: 100%;
  height: 222px;
  position: relative;
  background: ${({ backgroundImage }) =>
    backgroundImage
      ? `linear-gradient(#00000080, #00000080), url(${backgroundImage})`
      : "#00000080"};
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  border-radius: 15px;
  cursor: pointer;
  transition:
    filter 0.3s ease,
    transform 0.3s ease;

  &:hover {
    filter: brightness(1.2); /* 밝기 증가 효과 */
    transform: scale(1.02); /* 살짝 확대 효과 */
  }
`;
